import { useState, useEffect } from "react";
import { getVisitorId } from "@/lib/tracking";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const [offer, setOffer] = useState(null);
  const direct = params.get("mode") === "direct" && !!params.get("plan");
  const invite = params.get("invite");
  const [inv, setInv] = useState(null);
  useEffect(() => { if (invite) api.get(`/invitations/${invite}`).then((r) => { setInv(r.data); setForm((f) => ({ ...f, name: `${r.data.first_name} ${r.data.last_name}`.trim(), company_name: r.data.company_name, email: r.data.email })); }).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Invitation invalide")); }, [invite]);
  useEffect(() => { const c = params.get("plan"); if (direct && c) api.get(`/billing/preview/${c}`).then((r) => setOffer(r.data)).catch(() => setOffer(null)); }, [params, direct]);

  useEffect(() => { if (user && !direct) navigate("/app/dashboard"); }, [user, navigate, direct]);
  useEffect(() => { api.post("/track/event", { name: "signup_started", visitor_id: getVisitorId(), props: { plan: params.get("plan"), mode: direct ? "direct" : "trial" } }).catch(() => {}); }, [params, direct]);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await register({ ...form, email: form.email.trim(), mode: direct ? "direct" : "trial", plan_choice: direct ? params.get("plan") : undefined, invite_token: invite || undefined, visitor_id: getVisitorId() || undefined });
      toast.success(direct ? "Compte créé — dernière étape : le paiement sécurisé" : inv ? `Compte créé — votre accès gratuit Respondia PRO de ${inv.days} jours commence !` : "Compte créé — votre essai gratuit de 7 jours commence !");
      navigate(direct ? `/app/billing/start?plan=${params.get("plan")}` : "/app/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-blue-700 to-indigo-600 text-white relative overflow-hidden">
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        <Logo light />
        <div className="relative">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Votre premier audit en 2 minutes.</h2>
          <ul className="mt-6 space-y-3 text-blue-50">
            {["Un établissement DÉMO prêt à explorer", "Score digital et priorités immédiates", "Plan d'action clair et mesurable"].map((t, i) => (
              <li key={i} className="flex items-center gap-2"><Check className="w-5 h-5" /> {t}</li>
            ))}
          </ul>
        </div>
        <p className="relative text-sm text-blue-100">© 2026 Respondia — Pilotage & Performance</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-heading text-3xl font-bold">Créer votre compte</h1>
          <p className="mt-2 text-slate-500">{direct ? "Dernière étape avant le paiement sécurisé." : "Gratuit, sans engagement."}</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            {!direct && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="register-trial-terms">
                <div className="font-semibold text-emerald-900">{inv ? `${inv.days} jours d'accès gratuit à Respondia PRO — sur invitation` : "7 jours d'essai gratuit — sans carte bancaire"}</div>
                <p className="text-xs text-emerald-900/80 mt-1">Accès complet à Respondia Pro pendant {inv ? inv.days : 7} jours. Aucun prélèvement, aucune carte demandée. À la fin de l'essai, vos données sont conservées et vous choisissez librement de continuer (59,90 € TTC/mois) ou non.</p>
              </div>
            )}
            {offer && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="register-offer-terms">
                <div className="flex items-center justify-between gap-2"><span className="font-semibold text-blue-900">Offre {offer.plan.name}{offer.plan.badge ? ` · ${offer.plan.badge}` : ""}</span><span className="text-blue-800">{Number(offer.plan.price).toFixed(2).replace(".", ",")} € {offer.plan.price_suffix}</span></div>
                <p className="text-xs text-blue-900 mt-1" data-testid="register-direct-terms">{offer.terms}</p>
                <p className="text-[11px] text-blue-700/80 mt-1">{offer.stripe_connected ? "Après la création du compte, vous serez dirigé vers le paiement sécurisé Stripe pour enregistrer votre carte." : "Le paiement en ligne ouvre prochainement : votre choix d'offre est enregistré."}</p>
              </div>
            )}
            <div>
              <Label htmlFor="name">Votre nom</Label>
              <Input id="name" value={form.name} onChange={upd("name")} required data-testid="register-name-input" className="mt-1.5 h-11" placeholder="Jean Dupont" />
            </div>
            <div>
              <Label htmlFor="company">Nom de votre entreprise</Label>
              <Input id="company" value={form.company_name} onChange={upd("company_name")} required data-testid="register-company-input" className="mt-1.5 h-11" placeholder="Bistrot du Coin" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={upd("email")} required data-testid="register-email-input" className="mt-1.5 h-11" placeholder="vous@exemple.fr" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={form.password} onChange={upd("password")} required minLength={8} data-testid="register-password-input" className="mt-1.5 h-11" placeholder="8 caractères minimum" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-xl">
              {loading ? "Création..." : direct ? "Créer mon compte et continuer" : "Démarrer mon essai gratuit"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500 text-center">
            Déjà inscrit ? <Link to={params.get("plan") ? `/login?plan=${params.get("plan")}` : "/login"} className="text-blue-600 font-medium hover:underline" data-testid="to-login-link">Se connecter</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
